import { c as _c } from "react/compiler-runtime";
import type { EChartsOption, EChartsType } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from '@/hooks/use-translation';
import type { KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
import { buildGrid, buildKeyAxis, buildValueAxis, MONO_FONT, type AxisScale } from './echarts/build-base-option';
import type { ChartTokens } from './chart-tokens';
import { usePrintableChart } from './hooks/use-printable-chart';
import { useScreenPlotHeight } from './hooks/use-screen-plot-height';
import { PLOT_BOX_MARGIN } from './chart-geometry';
import { buildRangeNotes } from './echarts/build-range-notes';
import { SVG_RENDERER } from './renderer';
import type { KeyRangeNote, TouchDesignSerie } from './interfaces';
export enum TouchDesignSerieVariant {
  DefaultBold = 1,
  Default = 2,
  Target = 3,
  Measured = 4,
  Computed = 5,
}
export interface KeyboardChartProps {
  geometry: KeyboardPlacement;
  /** Plot height in pixels, excluding the keyboard underneath. */
  height: number;
  /** Called with the chart instance, to follow the pointer. */
  onChartReady?: (chart: EChartsType) => void;
  /** Which frame it sits in, which sets how much of a sheet is left for it. */
  variant?: 'analyze' | 'design';
  /** Stretches of the keyboard named on the plot, e.g. the keys with springs. */
  rangeNotes?: readonly KeyRangeNote[];
  scale: AxisScale;
  series: TouchDesignSerie[];
  /** A flat rule drawn across the plot, e.g. zero on the front weight chart. */
  ruleAt?: number;
}
const serieStyleByVariant = (serie: TouchDesignSerie, tokens: ChartTokens): Record<string, unknown> => {
  switch (serie.variant) {
    case TouchDesignSerieVariant.DefaultBold:
      return {
        endLabel: {
          color: tokens.inkMuted,
          fontFamily: MONO_FONT,
          fontSize: 11,
          formatter: serie.name,
          offset: [6, 0],
          show: true
        },
        lineStyle: {
          color: serie.color ?? tokens.seriesStandard,
          width: 1.5
        },
        type: 'line'
      };
    case TouchDesignSerieVariant.Default:
      return {
        lineStyle: {
          color: serie.color ?? tokens.seriesStandardSoft,
          width: 1
        },
        type: 'line'
      };
    case TouchDesignSerieVariant.Target:
      return {
        ...(serie.isFilled ? {
          areaStyle: {
            color: tokens.seriesTargetArea,
            opacity: 1
          }
        } : {}),
        lineStyle: {
          cap: 'round',
          color: serie.color ?? tokens.seriesTarget,
          width: 4
        },
        type: 'line',
        z: 5
      };
    case TouchDesignSerieVariant.Measured:
      return {
        itemStyle: {
          color: serie.color ?? tokens.ink
        },
        symbol: 'circle',
        symbolSize: 5,
        type: 'scatter',
        z: 6
      };
    case TouchDesignSerieVariant.Computed:
      return {
        itemStyle: {
          color: serie.color ?? tokens.inkMuted
        },
        symbol: 'diamond',
        symbolSize: 6,
        type: 'scatter',
        z: 6
      };
    default:
      return {
        type: 'line'
      };
  }
};

/**
 * A chart whose horizontal axis is the keyboard itself.
 *
 * Points are placed at the physical centre of their key rather than at their
 * key number, so that a measurement sits above the back of the key it was
 * taken on — which
 * a linear axis over 88 numbers would miss by up to a third of a key.
 */
export const KeyboardChart = t0 => {
  const $ = _c(48);
  const {
    geometry,
    height,
    onChartReady,
    rangeNotes,
    ruleAt,
    scale,
    series,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "analyze" : t1;
  const option = useRef(null);
  const screenBoxHeight = useScreenPlotHeight(height + PLOT_BOX_MARGIN, variant);
  const {
    onChartReady: bindChart,
    plotHeight: boxHeight,
    tokens
  } = usePrintableChart(variant, screenBoxHeight, option, onChartReady);
  const plotHeight = boxHeight - PLOT_BOX_MARGIN;
  const {
    currentLanguage
  } = useTranslation();
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== geometry || $[1] !== plotHeight || $[2] !== rangeNotes || $[3] !== ruleAt || $[4] !== scale || $[5] !== series || $[6] !== tokens) {
    let t6;
    if ($[11] !== geometry || $[12] !== tokens) {
      t6 = serie => ({
        data: serie.data.flatMap(point => {
          const place = geometry.keysByNumber.get(point.number)?.backCenter;
          return place === undefined ? [] : [[place, point.payload]];
        }),
        name: serie.name,
        showSymbol: false,
        silent: true,
        smooth: 0.3,
        ...serieStyleByVariant(serie, tokens)
      });
      $[11] = geometry;
      $[12] = tokens;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const echartsSeries = series.map(t6);
    let t7;
    if ($[14] !== ruleAt || $[15] !== tokens) {
      t7 = ruleAt === undefined ? [] : [{
        data: [],
        markLine: {
          data: [{
            yAxis: ruleAt
          }],
          label: {
            show: false
          },
          lineStyle: {
            color: tokens.axisStrong,
            type: "solid",
            width: 1.2
          },
          silent: true,
          symbol: "none"
        },
        name: "",
        showSymbol: false,
        silent: true,
        smooth: 0,
        type: "line"
      }];
      $[14] = ruleAt;
      $[15] = tokens;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const rule = t7;
    let t8;
    if ($[17] !== geometry || $[18] !== rangeNotes || $[19] !== scale || $[20] !== tokens) {
      t8 = rangeNotes?.length ? [buildRangeNotes(rangeNotes, geometry, scale, tokens)] : [];
      $[17] = geometry;
      $[18] = rangeNotes;
      $[19] = scale;
      $[20] = tokens;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    const notes = t8;
    t2 = false;
    if ($[22] !== plotHeight) {
      t3 = buildGrid(plotHeight);
      $[22] = plotHeight;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        show: false
      };
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    t5 = [...echartsSeries, ...rule, ...notes];
    $[0] = geometry;
    $[1] = plotHeight;
    $[2] = rangeNotes;
    $[3] = ruleAt;
    $[4] = scale;
    $[5] = series;
    $[6] = tokens;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      show: false
    };
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== geometry) {
    t7 = buildKeyAxis(geometry);
    $[26] = geometry;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== currentLanguage || $[29] !== scale || $[30] !== tokens) {
    t8 = buildValueAxis(tokens, scale, currentLanguage);
    $[28] = currentLanguage;
    $[29] = scale;
    $[30] = tokens;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t7 || $[37] !== t8) {
    t9 = {
      animation: t2,
      grid: t3,
      legend: t4,
      series: t5,
      tooltip: t6,
      xAxis: t7,
      yAxis: t8
    };
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const nextOption = t9 as EChartsOption;
  let t10;
  let t11;
  if ($[39] !== nextOption) {
    t10 = () => {
      option.current = nextOption;
    };
    t11 = [nextOption];
    $[39] = nextOption;
    $[40] = t10;
    $[41] = t11;
  } else {
    t10 = $[40];
    t11 = $[41];
  }
  useLayoutEffect(t10, t11);
  let t12;
  if ($[42] !== boxHeight) {
    t12 = {
      height: boxHeight,
      width: "100%"
    };
    $[42] = boxHeight;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== bindChart || $[45] !== nextOption || $[46] !== t12) {
    t13 = <ReactECharts option={nextOption} notMerge={true} onChartReady={bindChart} opts={SVG_RENDERER} style={t12} />;
    $[44] = bindChart;
    $[45] = nextOption;
    $[46] = t12;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
};